import React, { FunctionComponent } from 'react';
import { ReasonEnum } from '@/utils/types';
import { appSettings } from '@visalex/configs';
import { emailPattern, Form, SelectOption, SelectProps } from '@visalex/forms';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { useTranslation } from 'next-i18next';

import { QuestionnaireHeader } from '../../Common/QuestionnaireHeader';

const formPrefix = 'common.forms';

export type FreeConsultationFinalPageProps = {
  countries: Array<string>[];
  menuPortalProps: Partial<SelectProps>;
};

export const FreeConsultationFinalPage: FunctionComponent<FreeConsultationFinalPageProps> = ({
  countries,
  menuPortalProps,
}) => {
  const { t } = useTranslation();
  const languages = appSettings.supportedLanguages.map((lang) => {
    return {
      label: t(`action-bar.locales.${lang.language}`),
      value: lang.language,
    };
  });

  const reasons = [
    ReasonEnum.REUNITE_WITH_FAMILY,
    ReasonEnum.WORK_FOR_OWN_COMPANY,
    ReasonEnum.RELOCATING_FOR_CURRENT_JOB,
    ReasonEnum.INVEST_IN_US_BUSINESS,
    ReasonEnum.STUDY_OR_VISIT,
    ReasonEnum.OTHER,
  ].map((reason) => {
    return {
      label: t(`free-consultation.${reason}`),
      value: reason,
    };
  });

  const nationalities: SelectOption[] = [];

  countries.forEach((country) => {
    const option = { label: country[2], value: country[0] };

    if (country[2]) {
      nationalities.push(option);
    }
  });

  return (
    <div>
      <QuestionnaireHeader
        title={t('free-consultation.header-title')}
        caption={t('free-consultation.header-caption')}
      />
      <Form.Input
        name="first-name"
        rules={{
          required: t(`${formPrefix}.first-name.rules.required`),
        }}
        placeholder={t(`${formPrefix}.first-name.placeholder`)}
      />

      <Form.Input
        name="last-name"
        rules={{
          required: t(`${formPrefix}.last-name.rules.required`),
        }}
        placeholder={t(`${formPrefix}.last-name.placeholder`)}
      />

      <Form.Input
        name="email"
        rules={{
          required: t(`${formPrefix}.email.rules.required`),
          pattern: {
            value: emailPattern,
            message: t(`${formPrefix}.email.rules.format`),
          },
        }}
        placeholder={t(`${formPrefix}.email.placeholder`)}
      />

      <Form.Select
        name="language"
        placeholder={t(`${formPrefix}.language.placeholder`)}
        rules={{
          required: t(`${formPrefix}.language.rules.required`),
        }}
        options={languages}
        {...menuPortalProps}
      />

      <Form.Select
        name="nationality"
        placeholder={t(`${formPrefix}.nationality.placeholder`)}
        rules={{
          required: t(`${formPrefix}.nationality.rules.required`),
        }}
        options={nationalities}
        {...menuPortalProps}
      />

      <Form.PhoneInput
        name="phone"
        rules={{
          required: t(`${formPrefix}.phone.rules.required`),
          validate: (value) => isValidPhoneNumber(value) || t(`${formPrefix}.phone.rules.format`),
        }}
        placeholder={t(`${formPrefix}.phone.placeholder`)}
      />

      <Form.Select
        name="reason"
        placeholder={t(`${formPrefix}.reason.placeholder`)}
        rules={{
          required: t(`${formPrefix}.reason.rules.required`),
        }}
        options={reasons}
        {...menuPortalProps}
      />

      <Form.TextArea name="comments" placeholder={t(`${formPrefix}.comments.placeholder`)} />
    </div>
  );
};
